import i18n from "../../i18n";

// Thin JSON client for the Election Atlas API admin endpoints.
// The JWT lives in localStorage; a 401 clears it so the router can bounce
// the operator back to the login screen.

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001";

const TOKEN_KEY = "ea_admin_token";

export function getToken() {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  window.localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(status, body) {
    // Controllers here use three shapes: { error, message }, { errors: [...] }
    // (Devise validation failures), and Devise's own default failure app,
    // which sends only { error: "<string>" } — e.g. sign-in's "Invalid
    // Email or password." Try each in turn before giving up on a generic
    // message; a bare `error` is only used when it's a string, since the
    // Intelligence API's { error: { code, message } } shape isn't one this
    // client talks to.
    const message =
      (body &&
        (body.message ||
          (body.errors || []).join(", ") ||
          (typeof body.error === "string" ? body.error : null))) ||
      i18n.t("admin.common.requestFailed", { status });
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = "GET", body, params } = {}) {
  const url = new URL(`${API_URL}${path}`);
  url.searchParams.set("locale", i18n.language);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    });
  }

  const headers = { Accept: "application/json" };
  if (body) headers["Content-Type"] = "application/json";
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    // Never let identity responses into the HTTP cache: a cached copy from
    // a sibling origin ships the wrong Access-Control-Allow-Origin.
    cache: "no-store",
  });

  if (response.status === 401) {
    clearToken();
  }

  let payload = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, payload);
  }

  return payload;
}

export const api = {
  signIn: (email, password) =>
    request("/api/v1/auth/sign_in", {
      method: "POST",
      body: { user: { email, password } },
    }),
  signOut: () => request("/api/v1/auth/sign_out", { method: "DELETE" }),
  me: () => request("/api/v1/me"),

  users: (params) => request("/api/v1/admin/users", { params }),
  user: (id) => request(`/api/v1/admin/users/${id}`),
  updateUser: (id, user) =>
    request(`/api/v1/admin/users/${id}`, { method: "PATCH", body: { user } }),
  extendTrial: (id, days, reason) => request(`/api/v1/admin/users/${id}/extend_trial`, {method: "POST", body: {days, reason}}),
  deleteUser: (id) => request(`/api/v1/admin/users/${id}`, { method: "DELETE" }),

  organizations: (params) => request("/api/v1/admin/organizations", { params }),
  organization: (id) => request(`/api/v1/admin/organizations/${id}`),
  createOrganization: (organization) =>
    request("/api/v1/admin/organizations", {
      method: "POST",
      body: { organization },
    }),
  updateOrganization: (id, organization) =>
    request(`/api/v1/admin/organizations/${id}`, {
      method: "PATCH",
      body: { organization },
    }),
  inviteToOrganization: (id, invite) =>
    request(`/api/v1/admin/organizations/${id}/invite`, {
      method: "POST",
      body: invite,
    }),
  deleteOrganizationMember: (organizationId, userId) =>
    request(`/api/v1/admin/organizations/${organizationId}/members/${userId}`, {
      method: "DELETE",
    }),
  resendOrganizationInvite: (organizationId, inviteId) =>
    request(`/api/v1/admin/organizations/${organizationId}/invites/${inviteId}/resend`, {
      method: "POST",
    }),
  issueInvoice: (id, invoice) =>
    request(`/api/v1/admin/organizations/${id}/issue_invoice`, {
      method: "POST",
      body: invoice,
    }),

  analytics: () => request("/api/v1/admin/analytics"),

  intelligenceModelCosts: (params) =>
    request("/api/v1/admin/intelligence/model_costs", { params }),
};

export default api;
